import { getPlayerSkinDef } from '../player-colors';

export type PieceKey =
  | 'king'
  | 'knight'
  | 'hero'
  | 'mage'
  | 'cavalry'
  | 'assassin'
  | 'peasant';

export type ProfileAvatarDef = {
  id: string;
  label: string;
  pieceKey: PieceKey;
  requiresSkinId?: string;
  free?: boolean;
};

const FREE_PIECES: PieceKey[] = [
  'king',
  'knight',
  'hero',
  'mage',
  'cavalry',
  'assassin',
  'peasant',
];

export const PROFILE_AVATARS: ProfileAvatarDef[] = [
  ...FREE_PIECES.map((pieceKey) => ({
    id: `avatar-${pieceKey}-free`,
    label: `${pieceKey[0].toUpperCase()}${pieceKey.slice(1)}`,
    pieceKey,
    free: true as const,
  })),
  {
    id: 'avatar-king-color-crimson',
    label: 'Crimson King',
    pieceKey: 'king',
    requiresSkinId: 'color-crimson',
  },
  {
    id: 'avatar-hero-color-azure',
    label: 'Azure Hero',
    pieceKey: 'hero',
    requiresSkinId: 'color-azure',
  },
  {
    id: 'avatar-knight-color-emerald',
    label: 'Emerald Knight',
    pieceKey: 'knight',
    requiresSkinId: 'color-emerald',
  },
  {
    id: 'avatar-king-skin-realm',
    label: 'Realm King',
    pieceKey: 'king',
    requiresSkinId: 'skin-realm',
  },
  {
    id: 'avatar-hero-skin-realm',
    label: 'Realm Hero',
    pieceKey: 'hero',
    requiresSkinId: 'skin-realm',
  },
  {
    id: 'avatar-store-sentinel',
    label: 'Gold Sentinel',
    pieceKey: 'mage',
  },
  {
    id: 'avatar-store-outrider',
    label: 'Outrider',
    pieceKey: 'cavalry',
  },
  {
    id: 'avatar-s1-scout',
    label: 'S1 Scout',
    pieceKey: 'peasant',
  },
  {
    id: 'avatar-s1-warden',
    label: 'S1 Warden',
    pieceKey: 'knight',
  },
  {
    id: 'avatar-s1-blade',
    label: 'S1 Blade',
    pieceKey: 'assassin',
  },
  {
    id: 'avatar-s1-marshal',
    label: 'S1 Marshal',
    pieceKey: 'cavalry',
  },
  {
    id: 'avatar-s1-legend',
    label: 'S1 Legend',
    pieceKey: 'king',
  },
];

export const DEFAULT_AVATAR_ID = 'avatar-king-free';

const byId = new Map(PROFILE_AVATARS.map((a) => [a.id, a]));

export function getProfileAvatarDef(avatarId?: string | null): ProfileAvatarDef {
  if (avatarId && byId.has(avatarId)) return byId.get(avatarId)!;
  return byId.get(DEFAULT_AVATAR_ID)!;
}

export function ownedSkinSet(ownedColorIds?: unknown): Set<string> {
  const list = Array.isArray(ownedColorIds) ? ownedColorIds.map(String) : [];
  return new Set(['color-slate', 'color-crimson', ...list]);
}

export function canUseAvatar(
  avatarId: string,
  ownedColorIds?: unknown,
  ownedIconIds?: unknown
): boolean {
  const def = byId.get(avatarId);
  if (!def) return false;
  if (def.free) return true;
  const ownedIcons = Array.isArray(ownedIconIds)
    ? ownedIconIds.map(String)
    : [];
  if (ownedIcons.includes(avatarId)) return true;
  if (!def.requiresSkinId) return false;
  return ownedSkinSet(ownedColorIds).has(def.requiresSkinId);
}

export function avatarFill(def: ProfileAvatarDef): string {
  if (def.requiresSkinId) return getPlayerSkinDef(def.requiresSkinId).fill;
  return '#c9a227';
}

export function avatarStroke(def: ProfileAvatarDef): string {
  if (def.requiresSkinId) return getPlayerSkinDef(def.requiresSkinId).stroke;
  return '#0a0e17';
}

export function listUsableAvatars(
  ownedColorIds?: unknown,
  ownedIconIds?: unknown
): ProfileAvatarDef[] {
  return PROFILE_AVATARS.filter((a) =>
    canUseAvatar(a.id, ownedColorIds, ownedIconIds)
  );
}
